import { MenuItem } from '@headlessui/react';
import Badge from '@src/components/atoms/badges/badge';
import Button from '@src/components/atoms/buttons/button';
import CopyableField from '@src/components/atoms/copiableField';
import Dropdown from '@src/components/atoms/dropdown/dropdown';
import Pagination from '@src/components/atoms/pagination';
import CustomizeToolTip from '@src/components/atoms/tooltip';
import { GovernanceActionFilterType } from '@src/models/enum';
import type { PaginatedGovernanceAction } from '@src/models/types';
import { getDaysUntilExpiry } from '@src/utils/date';
import { hexToBech32 } from '@src/utils/stringUtils';
import { useRouter } from 'next/navigation';

interface GovernanceActionProps {
  currentPage: number;
  setCurrentPage: (page: number) => void;
  proposalResponse: PaginatedGovernanceAction;
}
export default function GovernanceAction({
  currentPage,
  setCurrentPage,
  proposalResponse,
}: GovernanceActionProps) {
  const handlenextPage = () => {
    if (currentPage >= proposalResponse.totalCount) return;
    setCurrentPage(currentPage + 1);
  };
  const handlePreviousPage = () => {
    if (currentPage <= 1) return;
    setCurrentPage(currentPage - 1);
  };

  const router = useRouter();
  return (
    <div className="rounded-lg border p-4 dark:border-dark-neutral-700 dark:bg-dark-neutral-900">
      <div className="w-full overflow-auto">
        <table className="w-full rounded-lg border dark:border-dark-neutral-700">
          <thead>
            <tr>
              <th className="min-w-[150px]">Type</th>
              <th className="min-w-[200px]">Title</th>
              <th className="min-w-[80px]">Status</th>
              <th className="min-w-[200px]">Abstract</th>
              <th className="min-w-[141px]">Expiry</th>
              <th className="min-w-[143px] text-center">Action</th>
            </tr>
          </thead>
          <tbody>
            {proposalResponse.items.map((governanceAction, index) => {
              const { proposal, expireAt, meta, createdAt } = governanceAction;
              const isExpiredProposal = new Date(expireAt.time) < new Date();

              const proposalId =
                createdAt.index != null
                  ? `${createdAt.tx}#${createdAt.index}`
                  : createdAt.tx;

              const cip129GovernanceAction = hexToBech32(
                `${createdAt.tx}${index.toString(16).padStart(2, '0')}`,
                'gov_action'
              );

              const readableProposalType = Object.keys(
                GovernanceActionFilterType
              ).find((key) => {
                return key === proposal.type;
              })
                ? Object.values(GovernanceActionFilterType)[
                    Object.keys(GovernanceActionFilterType).indexOf(
                      proposal.type
                    )
                  ]
                : proposal.type;

              const generateMenuItem = () => {
                return [
                  <MenuItem key="action title">
                    <p className="label12 flex items-center justify-between gap-2 border border-dark-neutral-700 bg-dark-neutral-800 px-3 py-2 font-semibold text-gray-400">
                      Actions
                    </p>
                  </MenuItem>,
                  <MenuItem key="governance action Id">
                    <CopyableField
                      label="Governance Action ID"
                      textOnly
                      className="justify-between border-b border-dark-neutral-700 p-3 dark:bg-dark-neutral-600"
                      value={cip129GovernanceAction}
                      iconClassName="h-[18px] w-[18px]"
                      textClassName="font-medium dark:text-white max-w-[276px] body14"
                    />
                  </MenuItem>,
                  <MenuItem key="legacy governance action Id">
                    <CopyableField
                      label=" Legacy Governance Action ID (CIP-105)"
                      textOnly
                      className="justify-between p-3 dark:bg-dark-neutral-600"
                      value={proposalId}
                      iconClassName="h-[18px] w-[18px]"
                      textClassName="font-medium dark:text-white max-w-[276px] body14"
                    />
                  </MenuItem>,
                ];
              };
              const expiryDays = getDaysUntilExpiry(expireAt.time);
              return (
                <tr key={index}>
                  <td className="body14 font-medium dark:text-white">
                    {readableProposalType}
                  </td>

                  <td className="body14 font-medium dark:text-white">
                    <p
                      data-tooltip-place="bottom-start"
                      data-tooltip-id={proposalId + 'title'}
                      className="body14 max-w-[250px] truncate font-medium dark:text-white"
                    >
                      {meta.title || '-'}
                    </p>
                    {meta.title && meta.title.length > 40 && (
                      <CustomizeToolTip
                        id={proposalId + 'title'}
                        className="z-20 !max-w-[300px]"
                        description={meta.title}
                      />
                    )}
                  </td>
                  <td>
                    <Badge
                      className="w-fit"
                      label={isExpiredProposal ? 'Expired' : 'Live'}
                      color={isExpiredProposal ? 'error' : 'success'}
                    />
                  </td>
                  <td>
                    <p
                      data-tooltip-place="bottom-start"
                      data-tooltip-id={proposalId}
                      className="body14 max-w-[250px] truncate font-medium dark:text-white"
                    >
                      {meta.abstract || '-'}
                    </p>
                    {meta.abstract && meta.abstract.length > 40 && (
                      <CustomizeToolTip
                        id={proposalId}
                        className="z-20 !max-w-[300px]"
                        description={meta.abstract}
                      />
                    )}
                  </td>
                  <td className="body14 font-semibold dark:text-white">
                    {expiryDays}
                  </td>
                  <td className="flex items-center gap-2 px-3 py-[7px]">
                    <Button
                      onClick={() => {
                        router.push(`/governance_actions/${proposalId}`);
                      }}
                      buttonSize="xxsmall"
                      className="!min-w-[83px] !rounded-lg"
                      variant="neutral"
                    >
                      View Details
                    </Button>
                    <Dropdown
                      className="h-[34px] w-[34px] !rounded-lg !p-2"
                      verticalMenuIcon
                      contentWidth={300}
                      variant="neutral"
                      isDropDownIconEnable={false}
                      menuItems={generateMenuItem()}
                    ></Dropdown>
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>
      <Pagination
        page={currentPage}
        totalPages={parseInt(
          Math.ceil(proposalResponse.totalCount / 10).toFixed(0)
        )}
        onNext={handlenextPage}
        onPrevious={handlePreviousPage}
      />
    </div>
  );
}
